import { DOCUMENT } from "@angular/common";
import {
  DestroyRef,
  Directive,
  ElementRef,
  NgZone,
  inject,
  signal,
} from "@angular/core";

const PAGE_TOP_TOLERANCE = 8;
const EDITABLE_CONTROL =
  'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), textarea, select, iframe, [contenteditable="true"]';

/** Keep the mobile language selector visible on arrival, then let it scroll normally. */
@Directive({
  selector: "[appMobileFooter]",
  host: { "[class.app-footer--at-page-end]": "atPageEnd()" },
})
export class MobileFooterDirective {
  protected readonly atPageEnd = signal(false);

  private readonly document = inject(DOCUMENT);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  constructor() {
    const view = this.document.defaultView;
    if (!view) return;

    const update = () => {
      if (this.destroyRef.destroyed) return;

      const active = this.document.activeElement;
      const editing =
        !!active &&
        !this.element.nativeElement.contains(active) &&
        active.matches(EDITABLE_CONTROL);
      const atPageEnd = view.scrollY > PAGE_TOP_TOLERANCE || editing;

      // Scrolling must not trigger change detection on every frame.
      if (atPageEnd !== this.atPageEnd()) {
        this.zone.run(() => this.atPageEnd.set(atPageEnd));
      }
    };
    // Wait for focus to settle, including when it enters a Braintree iframe.
    const afterFocus = () => queueMicrotask(update);

    this.zone.runOutsideAngular(() => {
      view.addEventListener("scroll", update, { passive: true });
      view.addEventListener("blur", afterFocus);
      view.addEventListener("focus", afterFocus);
      this.document.addEventListener("focusin", afterFocus);
      this.document.addEventListener("focusout", afterFocus);
    });
    update();

    this.destroyRef.onDestroy(() => {
      view.removeEventListener("scroll", update);
      view.removeEventListener("blur", afterFocus);
      view.removeEventListener("focus", afterFocus);
      this.document.removeEventListener("focusin", afterFocus);
      this.document.removeEventListener("focusout", afterFocus);
    });
  }
}
